/**
 * CombatLab — the MEASURED arena for combat + VFX work (`combat-lab`).
 *
 * WHY THIS EXISTS (2026-07-26): the COMBAT-MELEE-CONE-02 probe measured its
 * per-class cone profiles against live `first_field` pigs, and between the
 * HP-before and HP-after reads the pigs MOVED — 75→72, 112→98, 177→126,
 * 257→255 px. A cone-reach measurement taken against a moving ruler cannot
 * settle anything. This map removes that: fixed anchors, targets that cannot
 * move, and distances that are exact IN THE COORDINATE SPACE COMBAT USES.
 *
 * ── WHAT v1.0 GOT WRONG, AND HOW (2026-07-26, DJ standing in the map) ──────
 * v1.0 shipped a ruler that was out by 45px at point blank. Two independent
 * errors, both invisible to a test that measured the map against its own
 * authoring numbers:
 *
 *   1. THE PLAYER WAS NEVER WHERE THE ANCHOR SAID. `type:'decorative'` stops
 *      COLLISION only: `GroundCheckSystem` still grounded on it and
 *      `MapSystem.validateOnGround` (the spawn snapper) still snapped to it.
 *      So this map's own 2px facing-axis marker at y=659 was both. Measured
 *      live, not deduced: `getDefaultSpawnPosition()` returned {700, 619} and
 *      the player sat there with `platformId === 'lab-facing-axis'` — 41px
 *      above the floor the whole ruler is measured from.
 *      Fix: every marker here is now `solid: false` as well (`DRAWING`), the
 *      explicit opt-out all three gates honour. Making `decorative` imply it
 *      globally is the correct end state but a separate packet — it would
 *      still remove accidental standing surfaces from shipped quest maps
 *      (Tidewend pig-pen rails, Lanternwood lamp glows, …). Drowned Road's
 *      platform-drawn props were cleaned in its 2026-07-30 map rework; see
 *      PlatformSolidity.ts.
 *
 *   2. THE TARGET POINT WAS THE WRONG CORNER. The client's cone
 *      (`CombatManager.getMeleeConePlan` → `EcsCombatTargets` → ECS transform)
 *      measures a mob at its SPRITE CENTRE, i.e. schema `(x + w/2, y + h/2)` =
 *      +30, +25 for a 60x50 dummy. v1.0 placed against the raw schema corner,
 *      so `lab_ruler_right_060` sat 105px away at 32.1° instead of 60px — and
 *      a rogue's ±27.5° arc missed it entirely. `meleeConePlan().hit` came
 *      back EMPTY on a map whose entire job is to be hittable.
 *
 * ── The coordinate space, stated once (and asserted, never assumed) ────────
 * `MeleeCone.isInCone` does honest trigonometry on two bare points. Which
 * point of a body each one names is the whole ballgame — see
 * `src/shared/logic/CombatAnchors.ts`, the single source for it:
 *
 *   ORIGIN (both sides)   player HITBOX CENTRE = `player.position` + (20, 20).
 *   TARGET (both sides)   mob SPRITE CENTRE = schema (x+30, y+25).
 *
 * v1.2 (COMBAT-MELEE-CONE-04) moved the ORIGIN off the hitbox corner and onto
 * the body centre, so this station has TWO y values and they are not
 * interchangeable: `RULER_ANCHOR` is where a real body STANDS (top-left, what
 * physics settles to and what `CombatLab.playerAnchor.test.ts` asserts), and
 * `RULER_ORIGIN` is where combat MEASURES FROM, 20px right and 20px down of it.
 * Every distance, tick, reach mark and protractor bearing below is solved from
 * the ORIGIN. Solving from the anchor is what made v1.0 a 45px lie, in the same
 * shape: a ruler is only true from the point somebody actually measures at.
 *
 * v1.1 shipped while the other two DISAGREED: the client cone read the sprite
 * centre and the server's area scan read the raw schema corner, 39px apart for
 * a 60x50 dummy. That is what this lab then caught in the wild —
 * `lab_ruler_right_240` bleeding 170 HP to a 190px warrior cone that never once
 * predicted it (2026-07-26, DJ hand-playing). COMBAT-MELEE-CONE-03 moved the
 * server onto the sprite centre, so ONE placement is now exactly D in both
 * spaces. `serverDistancePx` survives as the parity readout — measured through
 * the server's own `mobCombatPoint` helper and asserted equal to `distancePx`,
 * so a re-divergence surfaces as a failing number instead of lost HP.
 *
 * Ground targets are therefore NOT placed at round horizontal offsets. Their
 * combat point is solved for:
 *
 *      |dx| = sqrt(D² - dy²)   with dy = (rowTop + h/2) - anchorY
 *      authored x = anchorX ± |dx| - w/2
 *
 * so the runtime `hypot(dx, dy)` from a REAL player's position is exactly D.
 *
 * ── Stations ───────────────────────────────────────────────────────────────
 * 1. RULER (anchor x=700). 25px ticks of true combat distance out to ±350
 *    (past `antiCheat.maxAreaRadius`=300), highlighted profile reaches, a
 *    half-arc protractor, and two historical ground ladders. Current ruler
 *    counts alone coincide for warrior/paladin; combining the bench gives
 *    unique signatures 3/3/5/2, 3/3/4/2, 3/2/3/1 and 2/1/2/0 respectively.
 * 2. BENCH (anchor x=1600). A one-way ledge 60px up puts targets off the
 *    facing axis at known bearings, which a linear ruler cannot do (arcs
 *    differ 55°→120°). It also stacks enough targets in one wedge that
 *    `maxTargets` actually clamps — flat ground CANNOT (mob body separation
 *    forces ≥42px apart, so a rogue's retained 138px melee cone fits at most two).
 *
 * ── Why the targets hold still ─────────────────────────────────────────────
 * `training_dummy` (content/mobs/starter_zone.toml): moveSpeed 0, aggroRange
 * 0, hp raised to 999,999 here so a session never despawns a ruler mark. The
 * three drift paths are designed out and asserted:
 *   - player bump — needs AABB overlap; every target clears the player box at
 *     its station (this is why the near ruler mark is 80px, not 60: the
 *     centre-anchor correction pulls raw x 30px closer to the player).
 *   - mob-mob separation — needs <42px centre spacing AND vertical overlap;
 *     every same-row pair here is ≥45px apart, and the rows miss vertically.
 *   - spawn snap — `snapMobToNearestPlatform` skips `solid: false`, so no tick
 *     is a snap candidate at all any more. Belt and braces: each dummy is also
 *     authored with its feet exactly on its surface (vertical distance 0),
 *     which nothing could beat even if it were a candidate.
 *
 * Proof: `tests/server/maps/CombatLab.geometry.test.ts` (real server spawn,
 * real cone maths) + `CombatLab.playerAnchor.test.ts` (real physics settles a
 * real 40x40 body and measures FROM IT — the test v1.0 did not have).
 *
 * URL: http://localhost:3001/combatlab   (src/config/combatlab/CombatLabConfig.ts)
 */

import { Platform } from '../../shared/CharacterTypes';
import {
  LAB_FLOOR_TOP_Y, LAB_DUMMY_TYPE,
  RULER_ANCHOR, BENCH_ANCHOR, RULER_ORIGIN, BENCH_ORIGIN,
  GROUND_DY, BENCH_DY, GROUND_ROW_TOP_Y, BENCH_ROW_TOP_Y, BENCH_LEDGE_TOP_Y, BENCH_LEDGE_H,
  RULER_STEP_PX, RULER_MAX_PX, RULER_LABEL_STEP_PX, TICK_W, TICK_H,
  RULER_PROFILE_MARKS, PROTRACTOR_MARKS, LAB_TARGETS, offsetForDistance,
} from './combatLab/CombatLabTargets';
export {
  LAB_FLOOR_TOP_Y, LAB_PLAYER_H, LAB_PLAYER_W, LAB_DUMMY_W, LAB_DUMMY_H, LAB_DUMMY_TYPE,
  RULER_ANCHOR, BENCH_ANCHOR, RULER_ORIGIN, BENCH_ORIGIN,
  RULER_PROFILE_MARKS, PROTRACTOR_MARKS, LAB_TARGETS, offsetForDistance, bearingDeg,
} from './combatLab/CombatLabTargets';
export type { LabTarget } from './combatLab/CombatLabTargets';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';

// ============================================================================
// STAGE — flat, unlit, deterministic. No parallax, no atmosphere, no scene FX.
// ============================================================================
const MAP_ID = 'combat-lab';
const MAP_NAME = 'Combat Lab';
const VERSION = '1.2';

const TILE = 32;
const W_TILES = 70;                       // 2240px — both stations + margins
const H_TILES = 30;                       // 960px

const FLOOR_DEPTH = H_TILES * TILE - LAB_FLOOR_TOP_Y;
/** Effectively immortal: a VQA session must never despawn a ruler mark. */
const LAB_DUMMY_HP = 999_999;

// Palette: mid-dark neutral. Dark enough that bright VFX read, light enough
// that dark VFX read, and flat enough that two screenshots differ only by the
// thing under test.
const COLOR_BG        = '#171b26';
const COLOR_FLOOR     = '#39404f';
const COLOR_LEDGE     = '#4a536a';
const COLOR_TICK      = '#c9d4e6';   // plain ruler tick
const COLOR_TICK_MAJOR= '#ffffff';   // labelled ruler tick
const COLOR_AXIS      = '#5f6b85';   // 0° facing axis
const COLOR_ARC       = '#7ad0ff';   // protractor bearing marks
const COLOR_ORIGIN    = '#ffd166';   // station origin pillar

const entities = LAB_TARGETS.map((t) => ({
  type: LAB_DUMMY_TYPE,
  id: t.id,
  x: t.x,
  y: t.y,
  properties: {
    displayName: t.displayName,
    hp: LAB_DUMMY_HP,
    // Short respawn so a stray AoE that somehow chews 999,999 HP doesn't leave
    // a hole in the ruler for the rest of the session.
    respawnTime: 2000,
  },
}));

// ============================================================================
// PLATFORMS — floor first (it must win every spawn snap), then markers.
//
// Every marker below is `type:'decorative'` AND `solid: false`. Both are
// needed, and the second is the one v1.0 was missing: `decorative` stops
// COLLISION only. The ground check and the spawn snapper still treated the 2px
// facing-axis line as a surface, so the player spawned onto it at y=619 and the
// entire ruler was measured from an anchor nobody stood on. `solid: false` is
// the explicit "this is a drawing" flag all three gates (plus the server's mob
// spawn snapper) honour. Not optional decoration hygiene — load-bearing.
// ============================================================================

/** Marker platforms are DRAWINGS: no collision, no grounding, no snap target. */
const DRAWING = { type: 'decorative', solid: false } as const;

const platforms: Platform[] = [
  {
    id: 'lab-floor',
    x: 0,
    y: LAB_FLOOR_TOP_Y,
    width: W_TILES * TILE,
    height: FLOOR_DEPTH,
    type: 'normal',
    color: COLOR_FLOOR,
  },
  {
    // One-way: dummies land on top, the player passes straight up through it
    // and never bumps his head walking the bench station.
    id: 'lab-bench-ledge',
    x: BENCH_ANCHOR.x - 240,
    y: BENCH_LEDGE_TOP_Y,
    width: 480,
    height: BENCH_LEDGE_H,
    type: 'normal',
    color: COLOR_LEDGE,
    isOneWay: true,
  },
];

/** Origin pillar — marks the exact x a station's cone is MEASURED FROM (the
 *  body centre), which since CONE-04 is 20px right of the raw `player.x` the
 *  body stands at. */
function pushOriginPillar(origin: { x: number; y: number }, label: string): void {
  platforms.push({
    id: `lab-origin-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    x: origin.x - 2,
    y: origin.y - 90,
    width: 4,
    height: 90,
    ...DRAWING,
    color: COLOR_ORIGIN,
    label,
  });
}

pushOriginPillar(RULER_ORIGIN, `ORIGIN x=${RULER_ORIGIN.x}`);
pushOriginPillar(BENCH_ORIGIN, `BENCH x=${BENCH_ORIGIN.x}`);

/** The 0° facing axis — drawn through the player's hitbox CENTRE, which is the
 *  y the cone origin actually has since CONE-04. It therefore crosses the body
 *  rather than skimming his scalp; that IS the fix, drawn. */
platforms.push({
  id: 'lab-facing-axis',
  x: RULER_ORIGIN.x - RULER_MAX_PX - 4,
  y: RULER_ORIGIN.y - 1,
  width: RULER_MAX_PX * 2 + 8,
  height: 2,
  ...DRAWING,
  color: COLOR_AXIS,
});

/** Ruler ticks — each marks the x where a GROUND target's COMBAT POINT (its
 *  sprite centre, not its corner) is exactly N px from the anchor. */
for (let distance = RULER_STEP_PX; distance <= RULER_MAX_PX; distance += RULER_STEP_PX) {
  const dx = offsetForDistance(distance, GROUND_DY);
  if (dx === null) continue;
  const labelled = distance % RULER_LABEL_STEP_PX === 0;
  for (const side of [1, -1] as const) {
    platforms.push({
      id: `lab-tick-${side > 0 ? 'r' : 'l'}-${distance}`,
      x: RULER_ORIGIN.x + side * dx - TICK_W / 2,
      y: LAB_FLOOR_TOP_Y - TICK_H,
      width: TICK_W,
      height: TICK_H,
      ...DRAWING,
      color: labelled ? COLOR_TICK_MAJOR : COLOR_TICK,
      ...(labelled ? { label: String(distance) } : {}),
    });
  }
}

/**
 * Highlighted ticks at every shipped melee reach + the AoE anti-cheat cap.
 *
 * The labels are STAGGERED vertically (one rung per mark). Four of the five
 * reaches — 138/188/219/238 — live inside 100px of ruler, so drawn at a common
 * height their text overlapped into a smear. Since a label renders at
 * `platform.y - 14`, raising each mark's tick by a rung stacks the readings
 * instead. Only the tick's TOP moves; its base still meets the floor, so the
 * position it marks is unchanged.
 */
const REACH_LABEL_RUNG_PX = 17;
/**
 * The lowest rung clears the dummy NAMEPLATE band. MobRenderer puts a mob's
 * name ~50px above its centre (≈y 625 for the ground row) and a label renders
 * at `platform.y - 14`, so a ladder starting at the old TICK_H*2 (=36) put
 * a profile label straight through "160px". 92 starts the ladder at y=594.
 */
const REACH_LABEL_BASE_PX = 92;
RULER_PROFILE_MARKS.forEach((mark, rung) => {
  const dx = offsetForDistance(mark.distancePx, GROUND_DY);
  if (dx === null) return;
  const top = LAB_FLOOR_TOP_Y - REACH_LABEL_BASE_PX - rung * REACH_LABEL_RUNG_PX;
  for (const side of [1, -1] as const) {
    platforms.push({
      id: `lab-reach-${side > 0 ? 'r' : 'l'}-${mark.distancePx}`,
      x: RULER_ORIGIN.x + side * dx - TICK_W,
      y: top,
      width: TICK_W * 2,
      height: LAB_FLOOR_TOP_Y - top,
      ...DRAWING,
      color: mark.color,
      // LEFT side carries the text, both sides carry the mark. The ruler is
      // symmetric so one reading covers both, and it leaves the right half
      // clear for the protractor's bearing labels — which otherwise landed on
      // top of the warrior / paladin profile labels.
      ...(side < 0 ? { label: `${mark.distancePx} ${mark.note}` } : {}),
    });
  }
});

/**
 * Protractor — two marks per half-arc, at 120px and 200px along the bearing,
 * measured from the ORIGIN. Purely decorative: no collision, no projectile
 * block, and (post-fix) no standing on them either.
 */
const PROTRACTOR_RADII = [120, 200] as const;
const ARC_MARK = 8;
for (const mark of PROTRACTOR_MARKS) {
  const theta = (mark.halfArcDeg * Math.PI) / 180;
  for (const radius of PROTRACTOR_RADII) {
    const outer = radius === PROTRACTOR_RADII[PROTRACTOR_RADII.length - 1];
    platforms.push({
      id: `lab-arc-${mark.halfArcDeg}-${radius}`,
      x: RULER_ORIGIN.x + Math.cos(theta) * radius - ARC_MARK / 2,
      y: RULER_ORIGIN.y - Math.sin(theta) * radius - ARC_MARK / 2,
      width: ARC_MARK,
      height: ARC_MARK,
      ...DRAWING,
      color: COLOR_ARC,
      ...(outer ? { label: `${mark.halfArcDeg}° ${mark.note}` } : {}),
    });
  }
}

// ============================================================================
// LEVEL
// ============================================================================

const spawnPoints = [{ x: RULER_ANCHOR.x / TILE, y: RULER_ANCHOR.y / TILE }];
const defaultSpawn = { x: RULER_ANCHOR.x, y: RULER_ANCHOR.y };

export const CombatLab: GridLevel & Record<string, unknown> = {
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,

  widthTiles: W_TILES,
  heightTiles: H_TILES,
  width: W_TILES * TILE,
  height: H_TILES * TILE,
  tileSize: TILE,

  backgroundColor: COLOR_BG,
  gravity: 1000,

  // Single spawn point + matching defaultSpawn + type 'town' = the player lands
  // on the ruler origin on join AND on every respawn. 'field' would randomise
  // it, which would make the anchor a lie.
  spawnPoints,
  defaultSpawn,

  layers: [
    { name: LayerType.GROUND,    visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.OBJECTS,   visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.ENTITIES,  visible: true, locked: false, opacity: 1.0, tiles: new Map(), entities },
    { name: LayerType.TRIGGERS,  visible: true, locked: false, opacity: 1.0, tiles: new Map() },
  ],

  platforms,
  entities,
  spawnRules: [],
  type: 'town',

  // Explicit empty layer set wins outright in `resolveLayers` precedence — no
  // fireflies, no god rays, no weather. Two captures of the same swing differ
  // only by the swing.
  effectLayers: [],

  metadata: {
    author: 'DJ',
    description:
      'Measured combat/VFX lab. Ruler station: 25px ticks of TRUE combat distance out to 350px '
      + '(past the 300px AoE cap), highlighted melee reaches, a half-arc protractor, and two '
      + 'ground ladders combined with the bench for a unique per-class signature. Bench station: a '
      + 'one-way ledge putting targets off the facing axis at known bearings, plus a flat '
      + 'cluster dense enough to clamp maxTargets. Every target is an inert training_dummy.',
    tags: ['testing', 'combat', 'vfx', 'measurement', 'lab'],
    lobbyTools: { enabled: true },
    // Measurement lab: PvP off and bots ignore, exactly like the Ability
    // Grounds (K9) — a stray bot AoE would splash the ruler and pollute a read.
    profile: 'lobby_safe',
    overrides: {
      pvp: { enabled: false, botBehavior: 'ignore' },
    },
  },
};

export {
  MAP_ID,
  MAP_NAME,
  TILE,
  W_TILES,
  H_TILES,
  GROUND_DY,
  BENCH_DY,
  GROUND_ROW_TOP_Y,
  BENCH_ROW_TOP_Y,
  BENCH_LEDGE_TOP_Y,
  RULER_STEP_PX,
  RULER_MAX_PX,
};
